import { IDropZoneStepGroup } from 'interfaces/designOrchestration/designOrchestrationDiagram.interface';
import { IModuleOnDrag } from 'interfaces/designOrchestration/designOrchestrationModules.interface';

import {
  DROPZONES_NAMES,
  DROPZONE_TYPES,
  NEW_MODULE,
} from '../../DesignOrchestrationDiagram.constans';

export const generateNodeId = (baseNumber = 0) => {
  const timestamp = Date.now();
  return timestamp + Math.floor(Math.random() * 10000) + baseNumber;
};

// Extracted recursive function to find a step by parentId
export const findStepRecursive = (group: any, parentId: number): any => {
  if (!group) {
    return null;
  }

  // 1. Check in current group's steps
  const foundStep = findStepInGroup(group, parentId);
  if (foundStep) {
    return foundStep;
  }

  // 2. Check in nextStepGroup
  const nextGroupResult = group.nextStepGroup
    ? findStepRecursive(group.nextStepGroup, parentId)
    : null;
  if (nextGroupResult) {
    return nextGroupResult;
  }

  // 3. Check in nested and failback groups
  return findInSpecialGroups(group, parentId);
};

// Helper function to find a step directly in a group's steps array
const findStepInGroup = (group: any, parentId: number): any => {
  if (!group.steps) {
    return null;
  }

  return group.steps.find(
    (s: any) => s.workflowStepId?.toString() === parentId.toString()
  );
};

// Helper function to find a step in nested or failback groups
const findInSpecialGroups = (group: any, parentId: number): any => {
  if (!group.steps) {
    return null;
  }

  for (const step of group.steps) {
    // Check nestedStepGroup
    if (step.nestedStepGroup) {
      const result = findStepRecursive(step.nestedStepGroup, parentId);
      if (result) {
        return result;
      }
    }

    // Check failbackStepGroup
    if (step.failbackStepGroup) {
      const result = findStepRecursive(step.failbackStepGroup, parentId);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

// Get parent step from workflow using the recursive function
const getParentStep = (
  parentId: number,
  currentWorkflow?: any,
  isGlobalFailback = false
) => {
  if (!currentWorkflow) {
    return null;
  }

  if (isGlobalFailback) {
    return findStepRecursive(
      currentWorkflow?.workflow?.failbackStepGroup,
      parentId
    );
  }
  return findStepRecursive(currentWorkflow?.workflow?.nextStepGroup, parentId);
};

export const getAncestryChain = (
  parentId: number | null,
  currentWorkflow?: any,
  isGlobalFailback = false
) => {
  let ancestryChain: string[] = [];

  // If no parentId, return empty ancestry chain
  if (!parentId) {
    return ancestryChain;
  }

  // Add current parentId to chain
  ancestryChain.push(parentId.toString());

  // If workflow provided, look for parent's ancestry
  if (currentWorkflow) {
    const parentStep = getParentStep(
      parentId,
      currentWorkflow,
      isGlobalFailback
    );

    if (parentStep?.nestGroupId?.length > 0) {
      ancestryChain = [...parentStep.nestGroupId];
    }
  }

  return ancestryChain;
};

// SUCCEEDING
export function createNewSucceedingStep(
  module: IModuleOnDrag,
  id: string,
  failback: boolean,
  isGlobalFailback = false
): IDropZoneStepGroup {
  const newStepId = Object.values(DROPZONE_TYPES).includes(id as any)
    ? generateNodeId(1)
    : Number(id);
  const nestedFailbackId = generateNodeId(3);
  const parallelId = generateNodeId(5);
  const stepTitle = isGlobalFailback
    ? DROPZONES_NAMES.PARALLEL_GLOBAL_FAILBACK
    : failback
    ? DROPZONES_NAMES.PARALLEL_FAILBACK
    : DROPZONES_NAMES.PARALLEL;
  const stepId = isGlobalFailback
    ? DROPZONE_TYPES.PARALLEL_GLOBAL_FAILBACK
    : failback
    ? DROPZONE_TYPES.PARALLEL_FAILBACK
    : DROPZONE_TYPES.PARALLEL;

  return {
    ...NEW_MODULE,
    steps: [
      {
        ...NEW_MODULE.steps[0],
        stepTitle: module.moduleName,
        stepIndex: null,
        workflowStepId: newStepId,
        workflowStepGroupId: null,
        stepType: isGlobalFailback
          ? 'globalFailback'
          : failback
          ? 'failback'
          : 'step',
        uniqueId: module.uniqueId,
        module: {
          ...NEW_MODULE.steps[0].module,
          version: module.moduleVersion,
          name: module.moduleName,
          description: module.moduleDescription,
          id: module.id,
          textId: module.textId,
        },
        paramGroups: [
          {
            groupTitle: null,
            groupCondition: null,
            parameters: module.parameters.map((parameter) => ({
              parameter,
              value: parameter.value,
            })),
          },
        ],
        ...(failback || isGlobalFailback
          ? {}
          : {
              failbackStepGroup: {
                // NESTED FAILBACK
                ...NEW_MODULE,
                parentWorkflowStepGroupId: null,
                steps: [...NEW_MODULE.steps].map((step: any) => ({
                  ...step,
                  stepTitle: DROPZONES_NAMES.FAILBACK,
                  workflowStepId: nestedFailbackId,
                  stepId: DROPZONE_TYPES.NESTED_FAILBACK,
                  stepType: 'nestedFailback',
                  workflowStepGroupId: null,
                  associatedMainNodeId: newStepId,
                })),
              },
            }),
      },
      {
        ...NEW_MODULE.steps[0],
        stepTitle,
        stepIndex: null,
        stepId,
        stepType: isGlobalFailback ? 'globalFailback' : 'step',
        workflowStepId: parallelId,
        workflowStepGroupId: null,
        associatedMainNodeId: newStepId,
      },
    ],
  };
}

export function createNewSucceedingFailbackStep(
  module: IModuleOnDrag,
  id: string,
  isGlobalFailback = false
): IDropZoneStepGroup {
  const newStepId = Object.values(DROPZONE_TYPES).includes(id as any)
    ? generateNodeId(1)
    : Number(id);
  const parallelId = generateNodeId(5);

  return {
    ...NEW_MODULE,
    steps: [
      {
        ...NEW_MODULE.steps[0],
        stepTitle: module.moduleName,
        stepIndex: null,
        workflowStepId: newStepId,
        workflowStepGroupId: null,
        stepType: isGlobalFailback ? 'globalFailback' : 'failback',
        uniqueId: module.uniqueId,
        module: {
          ...NEW_MODULE.steps[0].module,
          version: module.moduleVersion,
          name: module.moduleName,
          description: module.moduleDescription,
          id: module.id,
          textId: module.textId,
        },
        paramGroups: [
          {
            groupTitle: null,
            groupCondition: null,
            parameters: module.parameters.map((parameter) => ({
              parameter,
              value: parameter.value,
            })),
          },
        ],
      },
      {
        ...NEW_MODULE.steps[0],
        stepTitle: isGlobalFailback
          ? DROPZONES_NAMES.PARALLEL_GLOBAL_FAILBACK
          : DROPZONES_NAMES.PARALLEL_FAILBACK,
        stepIndex: null,
        stepId: isGlobalFailback
          ? DROPZONE_TYPES.PARALLEL_GLOBAL_FAILBACK
          : DROPZONE_TYPES.PARALLEL_FAILBACK,
        workflowStepId: parallelId,
        workflowStepGroupId: null,
        stepType: isGlobalFailback ? 'globalFailback' : 'failback',
      },
    ],
  };
}

export function createNewParallelStep(
  module: IModuleOnDrag,
  id: string,
  currentParentSteps: any[],
  failback: boolean,
  isGlobalFailback = false
): IDropZoneStepGroup {
  const newStepId = Object.values(DROPZONE_TYPES).includes(id as any)
    ? generateNodeId(1)
    : Number(id);
  const nestedFailbackId = generateNodeId(3);
  const parallelId = generateNodeId(5);
  const nestedId = generateNodeId(6);
  const dropzoneName = failback
    ? DROPZONES_NAMES.PARALLEL_FAILBACK
    : isGlobalFailback
    ? DROPZONES_NAMES.PARALLEL_GLOBAL_FAILBACK
    : DROPZONES_NAMES.PARALLEL;
  const dropzoneType = failback
    ? DROPZONE_TYPES.PARALLEL_FAILBACK
    : isGlobalFailback
    ? DROPZONE_TYPES.PARALLEL_GLOBAL_FAILBACK
    : DROPZONE_TYPES.PARALLEL;

  const currentSteps = currentParentSteps.filter(
    (steps) => steps.stepTitle !== dropzoneName
  );
  const workflowStepGroupId = currentSteps[0]?.workflowStepGroupId;

  return {
    ...NEW_MODULE,
    steps: [
      ...currentSteps.map((currentStep) => {
        const currentStepNestedId = generateNodeId(6);
        return {
          ...currentStep,
          nestedStepGroup: currentStep?.nestedStepGroup
            ? currentStep.nestedStepGroup
            : {
                ...NEW_MODULE,
                parentWorkflowStepGroupId: null,
                steps: [...NEW_MODULE.steps].map((step: any) => ({
                  ...step,
                  stepTitle: isGlobalFailback
                    ? DROPZONES_NAMES.GLOBAL_FAILBACK_STEPS
                    : failback
                    ? DROPZONES_NAMES.FAILBACK
                    : DROPZONES_NAMES.NESTED,
                  workflowStepId: currentStepNestedId,
                  stepId: isGlobalFailback
                    ? DROPZONE_TYPES.NESTED_GLOBAL_FAILBACK
                    : failback
                    ? DROPZONE_TYPES.NESTED_FAILBACK
                    : DROPZONE_TYPES.NESTED,
                  stepType: isGlobalFailback
                    ? 'nestedGlobalFailback'
                    : 'nested',
                  workflowStepGroupId: currentStep.workflowStepGroupId,
                  associatedMainNodeId: currentStep.workflowStepId,
                })),
              },
        };
      }),
      {
        ...NEW_MODULE.steps[0],
        stepTitle: module.moduleName,
        stepType: isGlobalFailback
          ? 'globalFailback'
          : failback
          ? 'failback'
          : 'step',
        stepIndex: null,
        workflowStepId: newStepId,
        workflowStepGroupId: null,
        uniqueId: module.uniqueId,
        module: {
          ...NEW_MODULE.steps[0].module,
          version: module.moduleVersion,
          name: module.moduleName,
          description: module.moduleDescription,
          id: module.id,
          textId: module.textId,
        },
        paramGroups: [
          {
            groupTitle: null,
            groupCondition: null,
            parameters: module.parameters.map((parameter) => ({
              parameter,
              value: parameter.value,
            })),
          },
        ],
        ...(failback
          ? {
              nestedStepGroup: {
                // NESTED FAILBACK
                ...NEW_MODULE,
                parentWorkflowStepGroupId: null,
                steps: [...NEW_MODULE.steps].map((step: any) => ({
                  ...step,
                  stepTitle: DROPZONES_NAMES.FAILBACK,
                  workflowStepId: nestedFailbackId,
                  stepId: DROPZONE_TYPES.NESTED_FAILBACK,
                  stepType: 'nested',
                  workflowStepGroupId: null,
                  associatedMainNodeId: step.workflowStepId,
                })),
              },
            }
          : isGlobalFailback
          ? {
              nestedStepGroup: {
                ...NEW_MODULE,
                parentWorkflowStepGroupId: null,
                steps: [...NEW_MODULE.steps].map((step: any) => ({
                  ...step,
                  stepTitle: DROPZONES_NAMES.GLOBAL_FAILBACK_STEPS,
                  workflowStepId: nestedFailbackId,
                  stepId: DROPZONE_TYPES.NESTED_GLOBAL_FAILBACK,
                  stepType: 'nestedGlobalFailback',
                  workflowStepGroupId: null,
                  associatedMainNodeId: step.workflowStepId,
                })),
              },
            }
          : {
              failbackStepGroup: {
                // NESTED FAILBACK
                ...NEW_MODULE,
                parentWorkflowStepGroupId: null,
                steps: [...NEW_MODULE.steps].map((step: any) => ({
                  ...step,
                  stepTitle: DROPZONES_NAMES.FAILBACK,
                  workflowStepId: nestedFailbackId,
                  stepId: DROPZONE_TYPES.NESTED_FAILBACK,
                  stepType: 'nestedFailback',
                  workflowStepGroupId: null,
                  associatedMainNodeId: step.workflowStepId,
                })),
              },
              nestedStepGroup: {
                ...NEW_MODULE,
                parentWorkflowStepGroupId: null,
                steps: [...NEW_MODULE.steps].map((step: any) => ({
                  ...step,
                  stepTitle: DROPZONES_NAMES.NESTED,
                  workflowStepId: nestedId,
                  stepId: DROPZONE_TYPES.NESTED,
                  stepType: 'nested',
                  workflowStepGroupId: null,
                  associatedMainNodeId: step.workflowStepId,
                })),
              },
            }),
      },
      {
        ...NEW_MODULE.steps[0],
        stepTitle: dropzoneName,
        stepIndex: null,
        stepId: dropzoneType,
        stepType: isGlobalFailback ? 'globalFailback' : 'step',
        workflowStepId: parallelId,
        workflowStepGroupId,
        associatedMainNodeId: newStepId,
      },
    ],
  };
}

export function addSucceedingModule(
  module: IModuleOnDrag,
  id: string,
  currentWorkflow?: any,
  isGlobalFailback = false
): IDropZoneStepGroup {
  const succeddingStep = createNewSucceedingStep(
    module,
    id,
    false,
    isGlobalFailback
  );
  const predecessorId = generateNodeId(2);
  const succedingId = generateNodeId(3);

  const parentId = Object.values(DROPZONE_TYPES).includes(id as any)
    ? null
    : Number(id);

  const ancestryChain: string[] = getAncestryChain(
    parentId,
    currentWorkflow,
    isGlobalFailback
  );
  const mainNodeId = succeddingStep.steps[0].workflowStepId;

  const updatedSuccedingStep = {
    ...succeddingStep,
    steps: succeddingStep.steps?.map((step: any) => ({
      ...step,
      nestGroupId: [...ancestryChain],
      workflowStepGroupId: null,
      associatedMainNodeId: mainNodeId,
    })),
  };

  return {
    // PREDECESSOR
    ...NEW_MODULE,
    steps: [...NEW_MODULE.steps].map((step: any) => ({
      ...step,
      stepTitle: isGlobalFailback
        ? DROPZONES_NAMES.PREDECESSOR_FAILBACK
        : DROPZONES_NAMES.PREDECESSOR,
      workflowStepId: predecessorId,
      stepId: isGlobalFailback
        ? DROPZONE_TYPES.PREDECESSOR_FAILBACK
        : DROPZONE_TYPES.PREDECESSOR,
      stepType: isGlobalFailback ? 'globalFailback' : 'step',
      associatedMainNodeId: mainNodeId,
      workflowStepGroupId: null,
      nestGroupId: [
        updatedSuccedingStep.steps[0].workflowStepId.toString(),
        ...ancestryChain,
      ].filter(Boolean),
    })),
    nextStepGroup: {
      // ADDED ONE
      ...updatedSuccedingStep,
      nextStepGroup: {
        // SUCCEEDING
        ...NEW_MODULE,
        steps: [...NEW_MODULE.steps].map((step: any) => ({
          ...step,
          stepTitle: DROPZONES_NAMES.SUCCEDING,
          workflowStepId: succedingId,
          stepId: DROPZONE_TYPES.SUCCEDING,
          associatedMainNodeId: mainNodeId,
          stepType: isGlobalFailback ? 'globalFailback' : 'step',
          workflowStepGroupId: null,
          nestGroupId: [
            updatedSuccedingStep.steps[0].workflowStepId.toString(),
            ...ancestryChain,
          ].filter(Boolean),
        })),
      },
    },
  };
}

export function addNestedModule(
  module: IModuleOnDrag,
  id: string
): IDropZoneStepGroup {
  const succeddingStep = createNewSucceedingStep(module, id, false);
  const predecessorId = generateNodeId(4);
  const succedingId = generateNodeId(5);

  const parentId = Number(id);

  const updatedSucceddingStep = {
    ...succeddingStep,
    steps: succeddingStep.steps?.map((step: any) => ({
      ...step,
      workflowStepGroupId:
        step.nestGroupId?.[step.nestGroupId.length - 1] || parentId,
      nestGroupId: [parentId.toString(), ...(step.nestGroupId || [])].filter(
        Boolean
      ),
    })),
  };
  const mainNodeId = updatedSucceddingStep.steps[0].workflowStepId;

  return {
    // PREDECESSOR
    ...NEW_MODULE,
    steps: [...NEW_MODULE.steps].map((step: any) => ({
      ...step,
      stepTitle: DROPZONES_NAMES.PREDECESSOR,
      workflowStepId: predecessorId,
      stepId: DROPZONE_TYPES.PREDECESSOR,
      associatedMainNodeId: mainNodeId,
      workflowStepGroupId:
        updatedSucceddingStep.steps[0].nestGroupId[
          updatedSucceddingStep.steps[0].nestGroupId.length - 1
        ],
      nestGroupId: [
        updatedSucceddingStep.steps[0].workflowStepId.toString(),
        parentId.toString(),
      ].filter(Boolean),
    })),
    nextStepGroup: {
      // ADDED ONE
      ...updatedSucceddingStep,
      nextStepGroup: {
        // SUCCEEDING
        ...NEW_MODULE,
        steps: [...NEW_MODULE.steps].map((step: any) => ({
          ...step,
          stepTitle: DROPZONES_NAMES.SUCCEDING,
          workflowStepId: succedingId,
          stepId: DROPZONE_TYPES.SUCCEDING,
          associatedMainNodeId: mainNodeId,
          workflowStepGroupId:
            updatedSucceddingStep.steps[0].nestGroupId[
              updatedSucceddingStep.steps[0].nestGroupId.length - 1
            ],
          nestGroupId: [
            updatedSucceddingStep.steps[0].workflowStepId.toString(),
            parentId.toString(),
          ].filter(Boolean),
        })),
      },
    },
  };
}

export function addNestedFailback(
  module: IModuleOnDrag,
  id: string,
  subcceeding: boolean,
  currentWorkflow?: any,
  isGlobalFailback = false
): IDropZoneStepGroup {
  const succeddingStep = createNewSucceedingFailbackStep(
    module,
    id,
    isGlobalFailback
  );
  const predecessorId = generateNodeId(2);
  const succedingId = generateNodeId(3);

  const parentId = Number(id);
  const ancestryChain: string[] = getAncestryChain(
    parentId,
    currentWorkflow,
    isGlobalFailback
  );

  const updatedSucceddingStep = {
    ...succeddingStep,
    steps: succeddingStep.steps?.map((step: any) => ({
      ...step,
      workflowStepGroupId: subcceeding
        ? null
        : step.nestGroupId?.[step.nestGroupId.length - 1] || parentId,
      nestGroupId: [...ancestryChain],
    })),
  };
  const mainNodeId = updatedSucceddingStep.steps[0].workflowStepId;

  return {
    // PREDECESSOR
    ...NEW_MODULE,
    steps: [...NEW_MODULE.steps].map((step: any) => ({
      ...step,
      stepTitle: DROPZONES_NAMES.PREDECESSOR_FAILBACK,
      workflowStepId: predecessorId,
      stepId: DROPZONE_TYPES.PREDECESSOR_FAILBACK,
      associatedMainNodeId: mainNodeId,
      workflowStepGroupId:
        updatedSucceddingStep.steps[0].nestGroupId[
          updatedSucceddingStep.steps[0].nestGroupId.length - 1
        ],
      nestGroupId: [
        updatedSucceddingStep.steps[0].workflowStepId.toString(),
        ...ancestryChain,
      ].filter(Boolean),
      ...(isGlobalFailback ? { stepType: 'globalFailback' } : {}),
    })),
    nextStepGroup: {
      // ADDED ONE
      ...updatedSucceddingStep,
      nextStepGroup: {
        // SUCCEEDING
        ...NEW_MODULE,
        steps: [...NEW_MODULE.steps].map((step: any) => ({
          ...step,
          stepTitle: DROPZONES_NAMES.SUCCEDING_FAILBACK,
          workflowStepId: succedingId,
          stepId: isGlobalFailback
            ? DROPZONE_TYPES.GLOBAL_FAILBACK_STEPS
            : DROPZONE_TYPES.SUCCEDING_FAILBACK,
          associatedMainNodeId: mainNodeId,
          workflowStepGroupId:
            updatedSucceddingStep.steps[0].nestGroupId[
              updatedSucceddingStep.steps[0].nestGroupId.length - 1
            ],
          nestGroupId: [
            updatedSucceddingStep.steps[0].workflowStepId.toString(),
            ...ancestryChain,
          ].filter(Boolean),
          ...(isGlobalFailback ? { stepType: 'globalFailback' } : {}),
        })),
      },
    },
  };
}

export function addParallelModule(
  module: IModuleOnDrag,
  id: string,
  currentParent: any,
  failback: boolean,
  isGlobalFailback = false
): IDropZoneStepGroup {
  // Find the actual parallel dropzone step that was clicked
  const parallelDropzoneStep = currentParent.steps.find(
    (step: any) => step.stepId === id || step.workflowStepId?.toString() === id
  );

  // Extract the nesting context from the dropzone itself
  const contextNestGroupId = parallelDropzoneStep?.nestGroupId || [];
  const contextAssociatedMainNodeId =
    parallelDropzoneStep?.associatedMainNodeId;

  // Create the basic parallel structure (new module + copied old ones + new dropzone)
  const parallelStep = createNewParallelStep(
    module,
    id,
    currentParent.steps,
    failback,
    isGlobalFailback
  );
  // Find the index of the newly added module step (usually second to last)
  const newModuleStepIndex = parallelStep.steps.findIndex(
    (_, index: number) => index === parallelStep.steps.length - 2 // Heuristic: second-to-last step
  );

  // Find the index of the new parallel dropzone step (last one)
  const newParallelDropzoneIndex = parallelStep.steps.length - 1;

  // Update the steps array, applying correct context only where needed
  const updatedSteps = parallelStep.steps?.map((step: any, index: number) => {
    if (index === newModuleStepIndex) {
      // Apply the dropzone's context to the new module step
      const newModuleWorkflowId = step.workflowStepId;
      return updateNestedGroups(step, newModuleWorkflowId, contextNestGroupId);
    } else if (index === newParallelDropzoneIndex) {
      return {
        ...step,
        nestGroupId: [...contextNestGroupId],
        associatedMainNodeId: contextAssociatedMainNodeId,
      };
    } else {
      return step;
    }
  });

  const updatedParallelStep: any = {
    ...parallelStep,
    steps: updatedSteps,
  };

  return {
    ...updatedParallelStep,
    nextStepGroup: {
      ...currentParent.nextStepGroup,
      steps: currentParent.nextStepGroup?.steps?.map((step: any) => ({
        ...step,
      })),
    },
  };
}

// Helper to build a new preceding step group when dropping into a preceding dropzone
export function createNewPrecedingStepGroup(
  module: IModuleOnDrag,
  id: string,
  currentParentGroup: IDropZoneStepGroup,
  failback: boolean,
  isGlobalFailback: boolean,
  currentWorkflow?: any
): IDropZoneStepGroup {
  // Preserve any existing children beyond this dropzone
  const originalNextStepGroup = currentParentGroup.nextStepGroup;

  // Build the core module group (with nested failback and parallel) using existing factory
  const baseModuleGroup = createNewSucceedingStep(
    module,
    id,
    failback,
    isGlobalFailback
  );
  const predecessorId = generateNodeId(2);
  const succedingId = generateNodeId(3);

  // Determine ancestry chain if provided
  const parentId = Object.values(DROPZONE_TYPES).includes(id as any)
    ? null
    : Number(id);

  const ancestryChain: string[] = getAncestryChain(
    parentId,
    currentWorkflow,
    isGlobalFailback
  );

  // Main node under which nested and parallel live
  const mainNodeId = baseModuleGroup.steps[0].workflowStepId;

  // Update nestGroupId and associations on the module's children
  const updatedModuleGroup: any = {
    ...baseModuleGroup,
    steps: baseModuleGroup.steps.map((step: any) => ({
      ...step,
      nestGroupId: [...ancestryChain],
      associatedMainNodeId: mainNodeId,
      workflowStepGroupId: null,
    })),
  };

  const stepTitle = failback
    ? DROPZONES_NAMES.PREDECESSOR_FAILBACK
    : DROPZONES_NAMES.PREDECESSOR;
  const stepId = failback
    ? DROPZONE_TYPES.PREDECESSOR_FAILBACK
    : DROPZONE_TYPES.PREDECESSOR;

  // Build the new structure: Predecessor → Module group → Predecessor → original children
  const newSucceedingGroup = {
    ...NEW_MODULE,
    steps: NEW_MODULE.steps?.map((step: any) => ({
      ...step,
      stepTitle,
      stepId,
      workflowStepId: succedingId,
      associatedMainNodeId: mainNodeId,
      nestGroupId: [mainNodeId.toString(), ...ancestryChain].filter(Boolean),
      workflowStepGroupId: null,
      ...(isGlobalFailback ? { stepType: 'globalFailback' } : {}),
    })),
    // Attach original children under the succeeding dropzone
    nextStepGroup: originalNextStepGroup || null,
  };

  return {
    // Preceding dropzone placeholder
    ...NEW_MODULE,
    steps: [
      {
        ...NEW_MODULE.steps[0],
        stepTitle,
        stepId,
        workflowStepId: predecessorId,
        associatedMainNodeId: mainNodeId,
        nestGroupId: [mainNodeId.toString(), ...ancestryChain].filter(Boolean),
        workflowStepGroupId: null,
        ...(isGlobalFailback ? { stepType: 'globalFailback' } : {}),
      },
    ],
    nextStepGroup: {
      // The module with its nested failback and parallel
      ...updatedModuleGroup,
      nextStepGroup: newSucceedingGroup,
    },
  };
}

export function addPredecessorModule(
  module: IModuleOnDrag,
  id: string,
  currentParentGroup: IDropZoneStepGroup,
  failback: boolean,
  isGlobalFailback = false,
  currentWorkflow?: any
): IDropZoneStepGroup {
  // Delegate to the new preceding step group builder, preserving existing children
  return createNewPrecedingStepGroup(
    module,
    id,
    currentParentGroup,
    failback,
    isGlobalFailback,
    currentWorkflow
  );
}

// Helper function for applying context to nested step groups
const updateNestedGroups = (
  step: any,
  parentWorkflowId: number,
  parentContext: string[]
) => {
  // Create updated step with proper nested context
  return {
    ...step,
    nestGroupId: [...parentContext],
    failbackStepGroup: step.failbackStepGroup
      ? {
          ...step.failbackStepGroup,
          steps: step.failbackStepGroup.steps.map((dzStep: any) => ({
            ...dzStep,
            nestGroupId: [parentWorkflowId.toString(), ...parentContext],
            associatedMainNodeId: parentWorkflowId,
          })),
        }
      : null,
    nestedStepGroup: step.nestedStepGroup
      ? {
          ...step.nestedStepGroup,
          steps: step.nestedStepGroup.steps.map((dzStep: any) => ({
            ...dzStep,
            nestGroupId: [parentWorkflowId.toString(), ...parentContext],
            associatedMainNodeId: parentWorkflowId,
            workflowStepGroupId: step.workflowStepGroupId,
          })),
        }
      : null,
  };
};
